import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { services } from "@/lib/services-data";
import { imgSize } from "@/lib/image-dimensions";
import { ArrowRight, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";

const FEATURED_COUNT = 6;

const ServicesSection = () => {
  const { openQuoteForm } = useQuoteForm();
  const featured = services.slice(0, FEATURED_COUNT);

  return (
    <section id="services" className="relative section-padding section-soft overflow-hidden">
      <div className="container-max relative">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14 max-w-[1200px] mx-auto">
          <div className="max-w-2xl">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="eyebrow-pill mb-5"
            >
              What We Do
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-heading text-foreground tracking-tight font-bold leading-[1.05]"
              style={{ fontSize: "clamp(2.25rem, 4.5vw, 4rem)" }}
            >
              Our <span className="text-gradient-primary">Services</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-muted-foreground max-w-[580px] text-lg leading-relaxed mt-5"
            >
              Twelve services covering the repairs, installs, and upgrades most homes actually
              need — from a single sticking door to a full bathroom rebuild.
            </motion.p>
          </div>
          <Button asChild size="lg" variant="outline" className="self-start lg:self-end gap-2 shrink-0">
            <Link to="/services">
              View All {services.length} Services <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7 max-w-[1200px] mx-auto items-stretch grid-center-last-3">
          {featured.map((service, i) => (
            <motion.article
              key={service.slug}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (i % 3) * 0.08, duration: 0.55, ease: [0.33, 1, 0.68, 1] }}
              className="group service-card w-full h-full flex flex-col rounded-2xl border border-border/70 bg-card p-3 shadow-[0_1px_2px_hsl(var(--accent-dark)/0.04),0_8px_24px_-12px_hsl(var(--accent-dark)/0.12)]"
              onTouchStart={(e) => e.currentTarget.classList.add("is-touched")}
              onTouchEnd={(e) => e.currentTarget.classList.remove("is-touched")}
            >
              <div className="media-frame" style={{ aspectRatio: "16 / 10" }}>
                <img
                  src={service.image}
                  alt={service.imageAlt}
                  loading="lazy"
                  decoding="async"
                  {...imgSize(service.image)}
                />
                <span className="corner tl" />
                <span className="corner tr" />
                <span className="corner bl" />
                <span className="corner br" />
                <span className="media-icon">
                  <Plus className="h-5 w-5" strokeWidth={2.25} />
                </span>
                <span className="absolute top-4 left-4 z-[3] inline-flex items-center rounded-full bg-white/95 text-foreground text-[11px] font-semibold uppercase tracking-wider px-3 py-1 border border-black/5">
                  {service.price}
                </span>
              </div>
              <div className="px-3 pt-6 pb-4 flex flex-col flex-1">
                <h3 className="font-heading text-2xl text-foreground tracking-tight font-bold leading-tight group-hover:text-primary transition-colors">
                  {/* Stretched link: one focusable target covering the whole card. */}
                  <Link
                    to={`/services/${service.slug}`}
                    className="after:absolute after:inset-0 after:z-[4] after:content-[''] rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    {service.title}
                  </Link>
                </h3>
                <p className="text-muted-foreground text-sm mt-2.5 leading-relaxed flex-1">{service.subtitle}</p>
                <span className="inline-flex items-center gap-2 text-primary font-bold text-sm mt-6 group-hover:gap-3 transition-all">
                  Learn More <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row justify-center items-center gap-3 mt-14">
          <Button size="lg" onClick={openQuoteForm} className="gap-2 btn-glow w-full sm:w-auto">
            Get Free Estimate <ArrowRight className="h-5 w-5" />
          </Button>
          <Button asChild size="lg" variant="outline" className="gap-2 w-full sm:w-auto">
            <Link to="/services">Browse All Services</Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
